/**
 * TagInput Component
 *
 * Multi-tag input with add/remove functionality
 * - Max 10 tags (configurable)
 * - Max 30 characters per tag (configurable)
 * - Optional validation function
 */

import React, { useState, KeyboardEvent } from 'react';
import { validateTag } from '../../utils/validation';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
  maxLength?: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  validate?: ((value: string) => string | null) | null;
}

export const TagInput: React.FC<TagInputProps> = ({
  tags,
  onChange,
  maxTags = 10,
  maxLength = 100,
  disabled = false,
  placeholder = 'Add a tag...',
  className = '',
  validate,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check if max tags reached
    if (tags.length >= maxTags) {
      setError(`Maximum ${maxTags} items allowed`);
      return;
    }

    // Check if tag already exists
    if (tags.includes(trimmedValue)) {
      setError('Item already exists');
      return;
    }

    // Check max length
    if (trimmedValue.length > maxLength) {
      setError(`Maximum ${maxLength} characters allowed`);
      return;
    }

    // Validate using provided validation function (or skip if null)
    if (validate) {
      const validationError = validate(trimmedValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Add tag
    onChange([...tags, trimmedValue]);
    setInputValue('');
    setError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      handleRemoveTag(tags.length - 1);
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    onChange(newTags);
    setError(null);
  };

  return (
    <div className={className}>
      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md bg-white min-h-[42px]">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded border border-blue-300"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(index)}
              disabled={disabled}
              className="hover:text-blue-900 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
              aria-label={`Remove tag ${tag}`}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleAddTag}
          disabled={disabled || tags.length >= maxTags}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] outline-none text-sm"
          aria-label="Add new tag"
        />
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <p className="mt-1 text-xs text-gray-500">
        {tags.length}/{maxTags} tags
      </p>
    </div>
  );
};
